/**
 * Espelho em Zod dos tipos do mundo do motor (`packages/engine/src/types.ts`).
 *
 * Por que existe: o motor é TypeScript puro e não valida nada em tempo de
 * execução. Tudo que atravessa a rede, em qualquer direção, passa por Zod antes
 * de virar objeto do mundo (SECURITY 4). Estes esquemas são a tradução fiel dos
 * tipos do motor, campo a campo, e os testes deste pacote comparam os dois
 * lados para que uma mudança lá quebre aqui em vez de quebrar em produção.
 *
 * Uma exceção proposital: `Player.potential` não aparece. O teto de força fica
 * escondido do treinador e só é revelado como faixa, dentro de um laudo de
 * olheiro (SERVER 5, SECURITY 4).
 *
 * Este pacote não importa o motor. Copiar a forma e conferir por teste é mais
 * barato do que carregar o motor inteiro dentro do cliente só para validar.
 */
import { z } from "zod";
import { IdSchema } from "./common.js";
// ---------------------------------------------------------------------------
// Jogadores
// ---------------------------------------------------------------------------
export const POSITIONS = [
    "GOL",
    "ZAG",
    "LD",
    "LE",
    "VOL",
    "MC",
    "MEI",
    "PD",
    "PE",
    "ATA",
];
export const PositionSchema = z.enum(POSITIONS);
/** Pé bom: direito, esquerdo ou ambidestro. */
export const FootSchema = z.enum(["D", "E", "A"]);
export const PERSONALITIES = [
    "lider",
    "profissional",
    "ambicioso",
    "temperamental",
    "mercenario",
    "tranquilo",
];
export const PersonalitySchema = z.enum(PERSONALITIES);
export const SquadRoleSchema = z.enum(["titular", "rotacao", "jovem"]);
/** Nota de um atributo. O motor mantém tudo entre 1 e 100. */
const AttrValueSchema = z.number().int().min(1).max(100);
/**
 * Os quatorze atributos. Todo jogador carrega os dez de linha e os quatro de
 * goleiro, mesmo que a posição só use uma parte deles.
 */
export const AttributesSchema = z.object({
    arm: AttrValueSchema,
    cab: AttrValueSchema,
    cru: AttrValueSchema,
    des: AttrValueSchema,
    dri: AttrValueSchema,
    fin: AttrValueSchema,
    mar: AttrValueSchema,
    pas: AttrValueSchema,
    res: AttrValueSchema,
    vel: AttrValueSchema,
    col: AttrValueSchema,
    sai: AttrValueSchema,
    ref: AttrValueSchema,
    pen: AttrValueSchema,
});
export const LoanTermsSchema = z.object({
    ownerId: IdSchema,
    untilSeason: z.number().int().min(0),
    salaryShare: z.number().finite().min(0).max(1),
});
export const ContractSchema = z.object({
    salary: z.number().finite().min(0),
    endsSeason: z.number().int().min(0),
    releaseClause: z.number().finite().min(0).nullable(),
    role: SquadRoleSchema,
});
export const PlayerSeasonStatsSchema = z.object({
    apps: z.number().int().min(0),
    goals: z.number().int().min(0),
    assists: z.number().int().min(0),
    yellow: z.number().int().min(0),
    red: z.number().int().min(0),
    minutes: z.number().int().min(0),
    ratingSum: z.number().finite().min(0),
    cleanSheets: z.number().int().min(0),
});
export const PlayerCareerStatsSchema = z.object({
    apps: z.number().int().min(0),
    goals: z.number().int().min(0),
    titles: z.number().int().min(0),
    clubs: z.number().int().min(0),
});
/**
 * Jogador como ele sai do servidor.
 *
 * É o `Player` do motor sem `potential`. O restante está na mesma ordem e com
 * os mesmos nomes, para que a comparação campo a campo continue possível.
 */
export const PlayerSchema = z.object({
    id: IdSchema,
    name: z.string().min(1),
    clubId: IdSchema.nullable(),
    nationality: z.string().min(1),
    pos: PositionSchema,
    age: z.number().int().min(14).max(60),
    foot: FootSchema,
    attrs: AttributesSchema,
    strength: z.number().int().min(1).max(100),
    personality: PersonalitySchema,
    injuryProneness: z.union([z.literal(0), z.literal(1), z.literal(2)]),
    morale: z.number().finite().min(0).max(100),
    condition: z.number().finite().min(0).max(100),
    contract: ContractSchema,
    injuryRounds: z.number().int().min(0),
    suspensionRounds: z.number().int().min(0),
    loan: LoanTermsSchema.nullable(),
    value: z.number().finite().min(0),
    shirt: z.number().int().min(1).max(99).nullable(),
    benchStreak: z.number().int().min(0),
    droughtRounds: z.number().int().min(0),
    transferListed: z.boolean(),
    season: PlayerSeasonStatsSchema,
    career: PlayerCareerStatsSchema,
});
// ---------------------------------------------------------------------------
// Clubes
// ---------------------------------------------------------------------------
/** 1 Municipal, 2 Estadual, 3 Regional, 4 Nacional, 5 Continental, 6 Mundial. */
export const ReputationSchema = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
]);
export const FacilityKeySchema = z.enum(["stadium", "youth", "training", "medical"]);
export const FacilityLevelSchema = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
]);
export const FacilitiesSchema = z.object({
    stadium: FacilityLevelSchema,
    youth: FacilityLevelSchema,
    training: FacilityLevelSchema,
    medical: FacilityLevelSchema,
});
export const BuildSchema = z.object({
    facility: FacilityKeySchema,
    targetLevel: FacilityLevelSchema,
    roundsLeft: z.number().int().min(0),
});
export const TacticStyleSchema = z.enum(["defensivo", "equilibrado", "ofensivo"]);
export const TacticMarkingSchema = z.enum(["leve", "normal", "forte"]);
export const TacticTempoSchema = z.enum(["baixo", "normal", "alto"]);
/** Uma vaga da formação personalizada: posição e ponto do campo, de 0 a 1. */
export const CustomSlotSchema = z
    .object({
    pos: PositionSchema,
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
})
    .strict();
export const TacticSchema = z.object({
    /** Nome da formação, como "4-3-3" ou "personalizada". O motor valida. */
    formation: z.string().min(1).max(16),
    /** Só na personalizada: as onze vagas, o goleiro na primeira. */
    custom: z.array(CustomSlotSchema).length(11).optional(),
    style: TacticStyleSchema,
    marking: TacticMarkingSchema,
    tempo: TacticTempoSchema,
    takers: z.object({
        penalty: IdSchema.nullable(),
        freeKick: IdSchema.nullable(),
        corner: IdSchema.nullable(),
    }),
    captainId: IdSchema.nullable(),
});
/** Substituição automática: entra `inId` no lugar de `outId` a partir do minuto. */
export const AutoSubSchema = z.object({
    outId: IdSchema,
    inId: IdSchema,
    minute: z.number().int().min(0).max(120),
});
export const LineupSchema = z.object({
    starters: z.array(IdSchema),
    bench: z.array(IdSchema),
    autoSubs: z.array(AutoSubSchema),
});
export const BoardExpectationSchema = z.object({
    sportingTarget: z.number().int().min(1),
    /** Chave de rótulo do motor (`objetivo.acesso`), não a frase. */
    sportingLabelKey: z.string(),
    financialTarget: z.number().finite(),
    patience: z.number().finite(),
});
export const ClubFinanceSchema = z.object({
    gate: z.number().finite(),
    members: z.number().finite(),
    tv: z.number().finite(),
    sponsor: z.number().finite(),
    prizes: z.number().finite(),
    transfersIn: z.number().finite(),
    wages: z.number().finite(),
    maintenance: z.number().finite(),
    interest: z.number().finite(),
    transfersOut: z.number().finite(),
    scouting: z.number().finite(),
});
/** Clássico entre dois clubes. A intensidade vai de 50 a 100. */
export const RivalrySchema = z.object({
    clubId: IdSchema,
    intensity: z.number().finite().min(0).max(100),
    label: z.string(),
});
/** Resultado recente: vitória, empate ou derrota. */
export const FormResultSchema = z.enum(["V", "E", "D"]);
/**
 * Clube completo, do jeito que o motor guarda.
 *
 * Só o clube do próprio membro sai assim. Os outros saem pela projeção pública
 * de `view.ts`, sem caixa, dívida, tática nem escalação.
 */
export const ClubSchema = z.object({
    id: IdSchema,
    name: z.string().min(1),
    short: z.string().min(1).max(8),
    countryId: IdSchema,
    reputation: ReputationSchema,
    founded: z.number().int(),
    rivals: z.array(RivalrySchema),
    fans: z.number().finite().min(0),
    colors: z.tuple([z.string(), z.string()]),
    cash: z.number().finite(),
    debt: z.number().finite().min(0),
    debtRate: z.number().finite().min(0).max(1),
    facilities: FacilitiesSchema,
    build: BuildSchema.nullable(),
    stadiumCapacity: z.number().int().min(0),
    ticketPrice: z.number().finite().min(0),
    managerId: IdSchema.nullable(),
    board: BoardExpectationSchema,
    tactic: TacticSchema,
    lineup: LineupSchema.nullable(),
    season: ClubFinanceSchema,
    form: z.array(FormResultSchema),
    titles: z.number().int().min(0),
});
// ---------------------------------------------------------------------------
// Competição e tabela
// ---------------------------------------------------------------------------
export const StandingRowSchema = z.object({
    clubId: IdSchema,
    played: z.number().int().min(0),
    wins: z.number().int().min(0),
    draws: z.number().int().min(0),
    losses: z.number().int().min(0),
    goalsFor: z.number().int().min(0),
    goalsAgainst: z.number().int().min(0),
    points: z.number().int(),
});
export const WorldPhaseSchema = z.enum(["pretemporada", "temporada", "postemporada"]);
// ---------------------------------------------------------------------------
// Mercado dentro do mundo
// ---------------------------------------------------------------------------
/**
 * Situação de uma proposta dentro do mundo do motor.
 *
 * O mercado entre humanos tem um vocabulário próprio, em inglês e com `vetoed`,
 * porque ele vive na tabela `offers` e conhece o veto do comissário, que o
 * motor não conhece. Ver `market.ts`.
 */
export const OfferStatusSchema = z.enum([
    "aberta",
    "aceita",
    "recusada",
    "contraproposta",
    "expirada",
]);
/** Proposta como o motor a guarda no mundo. */
export const OfferSchema = z.object({
    id: IdSchema,
    playerId: IdSchema,
    fromClubId: IdSchema,
    toClubId: IdSchema.nullable(),
    amount: z.number().finite().min(0),
    installments: z.number().int().min(1),
    salary: z.number().finite().min(0),
    years: z.number().int().min(1),
    status: OfferStatusSchema,
    counterAmount: z.number().finite().min(0).nullable(),
    createdRound: z.number().int().min(0),
    closesRound: z.number().int().min(0),
    /** Proposta a clube humano só fecha depois que ele responde. */
    approved: z.boolean(),
});
/** Laudo de olheiro. Entrega uma faixa de potencial, nunca o número exato. */
export const ScoutReportSchema = z.object({
    playerId: IdSchema,
    clubId: IdSchema,
    season: z.number().int().min(0),
    round: z.number().int().min(0),
    youthLevel: FacilityLevelSchema,
    potentialLow: z.number().int().min(1).max(100),
    potentialHigh: z.number().int().min(1).max(100),
    confidence: z.enum(["alta", "média", "baixa"]),
    /** Chave de rótulo do veredito (`olheiro.noTeto`), não a frase. */
    verdictKey: z.string(),
    /**
     * As observações do olheiro. `key: null` é laudo de save antigo, que guarda
     * a frase pronta; o resumo saiu, porque sai da faixa que já está aqui.
     */
    notes: z.array(z.union([
        z.object({ key: z.string() }),
        z.object({ key: z.null(), text: z.string() }),
    ])),
});
// ---------------------------------------------------------------------------
// Partida
// ---------------------------------------------------------------------------
export const MatchSideStatsSchema = z.object({
    possession: z.number().finite().min(0).max(100),
    chances: z.number().int().min(0),
    shots: z.number().int().min(0),
    goals: z.number().int().min(0),
    cards: z.number().int().min(0),
});
/**
 * Quanto um fator pesou no resultado, de 0 a 1, e qual fator foi.
 *
 * Chave e valores, e não a frase: o servidor não tem idioma, e quem mostra o
 * relatório é quem sabe em que língua a pessoa está jogando.
 */
export const MatchFactorSchema = z.object({
    impact: z.number().finite().min(0).max(1),
    key: z.string(),
    params: z.record(z.union([z.string(), z.number()])),
});
//# sourceMappingURL=world.js.map